import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  increment,
  runTransaction,
  setDoc,
} from 'firebase/firestore';
import {
  getDownloadURL,
  getMetadata,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { db, storage } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import {
  WotdVoiceConfig,
  normalizeWotdVoiceConfig,
  shouldUseVoiceDefaults,
} from '../../lib/aiVoice';
import { AdminTabs } from './VoiceAdmin';

/**
 * Wordbank admin — manage the four word lists the iOS scoring pipeline
 * pulls from. Each list is a Firestore subcollection at
 * `wordbank/{listId}/words/{word}` where the document ID IS the word.
 *
 *   - fillerwords   → penalize Confidence (heuristic + scoring prompt)
 *   - weakWords     → penalize Confidence (hedges)
 *   - confidentWords → boost Confidence (decisive verbs)
 *   - wod           → daily word-of-the-day rotation
 *
 * WordbankService on iOS fetches these at app launch and caches them,
 * so edits propagate after the app's next cold start (or a manual
 * pull-to-refresh once that's wired).
 *
 * The lower panel also manages onboarding chip selections:
 *   - wordbank/onboardingGoals/words/*
 *   - wordbank/onboardingFriction/words/*
 *   - category/* for starter categories
 */

type ListId = 'fillerwords' | 'weakWords' | 'confidentWords' | 'wod';
type OnboardingSelectionId = 'onboardingGoals' | 'onboardingFriction' | 'starterCategories';
type OnboardingSelectionSource = 'wordbank' | 'category';

type WotdAudioStatus = {
  storagePath: string;
  remoteURL: string | null;
  remoteUpdatedAt: number | null;
  generating: boolean;
  error: string | null;
};

type WotdWordStats = {
  timesWordOfTheDay: number;
  lastWordOfTheDayAt: number | null;
  lastWordOfTheDayBy: string | null;
  lastWordOfTheDayReason: string | null;
  lastWordOfTheDayDateKey: string | null;
};

type CurrentWotdSelection = {
  word: string | null;
  reason: string;
  dateKey: string | null;
  selectedAt: number | null;
  selectedBy: string | null;
  mode: 'manual' | 'automatic' | null;
  queuedWord: string | null;
  queuedReason: string;
  queuedUpdatedAt: number | null;
  queuedUpdatedBy: string | null;
  queuedSource: string | null;
};

type WotdPronunciationOverride = {
  alphabet: 'cmu-arpabet';
  phoneme: string;
};

const LISTS: Array<{ id: ListId; label: string; hint: string }> = [
  {
    id: 'fillerwords',
    label: 'Filler words',
    hint: 'Penalize confidence when present. Phrases OK ("you know", "i mean").',
  },
  {
    id: 'weakWords',
    label: 'Weak hedges',
    hint: 'Hedges like "maybe", "i guess" that signal the speaker isn\'t committing.',
  },
  {
    id: 'confidentWords',
    label: 'Confident language',
    hint: 'Decisive verbs and phrases that earn a confidence boost ("will", "must", "the answer is").',
  },
  {
    id: 'wod',
    label: 'Word of the Day pool',
    hint: "Pool the app chooses from. Feature today's word, generate English pronunciations, and watch how often each word has been featured.",
  },
];

type OnboardingSelectionMeta = {
  id: OnboardingSelectionId;
  label: string;
  hint: string;
  source: OnboardingSelectionSource;
  requiredCount: number;
  defaults?: string[];
};

type OnboardingOption = {
  id: string;
  label: string;
  order: number;
};

const DEFAULT_ONBOARDING_GOALS = [
  'Confidence',
  'Less filler',
  'Better structure',
  'Spontaneity',
  'Going off-script',
  'More poise',
  'Job interviews',
  'Toasts',
  'Wedding speech',
  'Sales pitch',
  'Standup',
  'Hosting events',
  'Presentations',
  'Storytelling',
  'Persuasion',
  'Stage presence',
  'Vocal projection',
  'Improvisation',
  'Reading audiences',
  'Public speaking',
  'Pacing',
  'Vocal variety',
  'Pausing',
  'Speaking less',
];

const DEFAULT_ONBOARDING_FRICTION = [
  'Nerves',
  'Filler words',
  'Going blank',
  'Pacing too fast',
  'Stage fright',
  'Rambling',
  'Vocal monotone',
  'Awkward pauses',
  'Forgetting points',
  'Sounding unprepared',
  'Off-topic tangents',
  'Eye contact',
];

const ONBOARDING_SELECTIONS: OnboardingSelectionMeta[] = [
  {
    id: 'onboardingGoals',
    label: 'Goals',
    hint: 'Step 1 chip cloud. The iOS app asks people to pick exactly 2.',
    source: 'wordbank',
    requiredCount: 2,
    defaults: DEFAULT_ONBOARDING_GOALS,
  },
  {
    id: 'onboardingFriction',
    label: 'Friction',
    hint: 'Step 2 chip cloud. The iOS app asks people to pick 1.',
    source: 'wordbank',
    requiredCount: 1,
    defaults: DEFAULT_ONBOARDING_FRICTION,
  },
  {
    id: 'starterCategories',
    label: 'Starter categories',
    hint:
      'Step 4 chip cloud. Uses the same Firestore category pool as the practice picker.',
    source: 'category',
    requiredCount: 3,
  },
];

const CONFIG_COLLECTION = 'app-config';
const WOTD_VOICE_CONFIG_DOC_ID = 'wotd-voice';
const WOTD_CURRENT_CONFIG_DOC_ID = 'wotd-current';

const wordSlug = (word: string) =>
  word
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const normalizeLabel = (value: string) => value.trim().replace(/\s+/g, ' ');

const categoryDocId = (label: string) => normalizeLabel(label).replace(/\//g, '-');

const numericOrder = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const numericValue = (value: unknown, fallback = 0) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;

const nullableString = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const currentUtcDayKey = () => new Date().toISOString().slice(0, 10);

const formatAdminTimestamp = (value: number | null) => {
  if (!value) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
};

const wotdAudioStoragePath = (word: string) => `wotd-pronunciations/${wordSlug(word)}.mp3`;

const CMU_PHONEME_PATTERN = /^[A-Z0-2 ]{2,160}$/;

const normalizeCmuPronunciation = (value: unknown) =>
  typeof value === 'string'
    ? value.trim().toUpperCase().replace(/\s+/g, ' ')
    : '';

const parseCmuPronunciation = (value: string) => {
  const phoneme = normalizeCmuPronunciation(value);
  return CMU_PHONEME_PATTERN.test(phoneme) ? phoneme : null;
};

const emptyAudioStatus = (word: string): WotdAudioStatus => ({
  storagePath: wotdAudioStoragePath(word),
  remoteURL: null,
  remoteUpdatedAt: null,
  generating: false,
  error: null,
});

const emptyWotdWordStats = (): WotdWordStats => ({
  timesWordOfTheDay: 0,
  lastWordOfTheDayAt: null,
  lastWordOfTheDayBy: null,
  lastWordOfTheDayReason: null,
  lastWordOfTheDayDateKey: null,
});

const emptyCurrentWotdSelection = (): CurrentWotdSelection => ({
  word: null,
  reason: '',
  dateKey: null,
  selectedAt: null,
  selectedBy: null,
  mode: null,
  queuedWord: null,
  queuedReason: '',
  queuedUpdatedAt: null,
  queuedUpdatedBy: null,
  queuedSource: null,
});

const wotdWordStatsFromData = (data: Record<string, unknown>): WotdWordStats => ({
  timesWordOfTheDay: numericValue(data.timesWordOfTheDay, numericValue(data.wotdSelectionCount)),
  lastWordOfTheDayAt: numericValue(data.lastWordOfTheDayAt, 0) || null,
  lastWordOfTheDayBy: nullableString(data.lastWordOfTheDayBy),
  lastWordOfTheDayReason: nullableString(data.lastWordOfTheDayReason),
  lastWordOfTheDayDateKey: nullableString(data.lastWordOfTheDayDateKey),
});

const currentWotdSelectionFromData = (data: Record<string, unknown>): CurrentWotdSelection => {
  const mode = nullableString(data.mode);
  return {
    word: nullableString(data.wordOfTheDay) ?? nullableString(data.word),
    reason: nullableString(data.reason) ?? nullableString(data.selectionReason) ?? '',
    dateKey: nullableString(data.dateKey) ?? nullableString(data.selectedDateKey),
    selectedAt: numericValue(data.selectedAt, numericValue(data.updatedAt)) || null,
    selectedBy: nullableString(data.selectedBy) ?? nullableString(data.updatedBy),
    mode: mode === 'manual' || mode === 'automatic' ? mode : null,
    queuedWord: nullableString(data.queuedWordOfTheDay) ?? nullableString(data.queuedWord),
    queuedReason: nullableString(data.queuedReason) ?? '',
    queuedUpdatedAt: numericValue(data.queuedUpdatedAt, 0) || null,
    queuedUpdatedBy: nullableString(data.queuedUpdatedBy),
    queuedSource: nullableString(data.queuedSource),
  };
};

const wotdPronunciationOverrideFromData = (
  data: Record<string, unknown>
): WotdPronunciationOverride | null => {
  const override = data.pronunciationOverride;
  if (override && typeof override === 'object') {
    const payload = override as Record<string, unknown>;
    const alphabet =
      typeof payload.alphabet === 'string'
        ? payload.alphabet.trim().toLowerCase()
        : 'cmu-arpabet';
    const phoneme = normalizeCmuPronunciation(payload.phoneme);
    if (alphabet === 'cmu-arpabet' && CMU_PHONEME_PATTERN.test(phoneme)) {
      return { alphabet: 'cmu-arpabet', phoneme };
    }
  }

  const legacyPhoneme = normalizeCmuPronunciation(data.cmuPronunciation);
  if (CMU_PHONEME_PATTERN.test(legacyPhoneme)) {
    return { alphabet: 'cmu-arpabet', phoneme: legacyPhoneme };
  }

  return null;
};

const remoteAgeLabel = (updatedAt: number | null) => {
  if (!updatedAt) return null;
  const minutes = Math.round((Date.now() - updatedAt) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
};

const cacheBustedAudioURL = (remoteURL: string, updatedAt: number | null) => {
  if (!updatedAt) return remoteURL;
  const separator = remoteURL.includes('?') ? '&' : '?';
  return `${remoteURL}${separator}v=${updatedAt}`;
};

const sortWotdWordsByPriority = (
  items: string[],
  statsByWord: Record<string, WotdWordStats>,
  excludeWords: Array<string | null> = []
) => {
  const excluded = new Set(
    excludeWords
      .filter((word): word is string => Boolean(word))
      .map((word) => word.toLowerCase())
  );
  return [...items]
    .filter((word) => !excluded.has(word.toLowerCase()))
    .sort((a, b) => {
      const aStats = statsByWord[a] ?? emptyWotdWordStats();
      const bStats = statsByWord[b] ?? emptyWotdWordStats();
      if (aStats.timesWordOfTheDay !== bStats.timesWordOfTheDay) {
        return aStats.timesWordOfTheDay - bStats.timesWordOfTheDay;
      }
      const aLast = aStats.lastWordOfTheDayAt ?? 0;
      const bLast = bStats.lastWordOfTheDayAt ?? 0;
      if (aLast !== bLast) return aLast - bLast;
      return a.localeCompare(b);
    });
};

const emptyOnboardingOptions = (): Record<OnboardingSelectionId, OnboardingOption[]> => ({
  onboardingGoals: [],
  onboardingFriction: [],
  starterCategories: [],
});

const sortOnboardingOptions = (items: OnboardingOption[]) =>
  [...items].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.label.localeCompare(b.label);
  });

const optionExists = (items: OnboardingOption[], label: string) =>
  items.some((item) => item.label.toLowerCase() === label.toLowerCase());

const OnboardingSelectionsPanel: React.FC<{ userEmail: string | null | undefined }> = ({
  userEmail,
}) => {
  const [active, setActive] = useState<OnboardingSelectionId>('onboardingGoals');
  const [options, setOptions] = useState<Record<OnboardingSelectionId, OnboardingOption[]>>(
    emptyOnboardingOptions
  );
  const [loadingLists, setLoadingLists] = useState<Set<OnboardingSelectionId>>(new Set());
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeMeta = ONBOARDING_SELECTIONS.find((item) => item.id === active)!;
  const activeOptions = useMemo(
    () => sortOnboardingOptions(options[active]),
    [active, options]
  );
  const isLoadingActive = loadingLists.has(active);

  const readOnboardingList = async (meta: OnboardingSelectionMeta): Promise<OnboardingOption[]> => {
    if (meta.source === 'category') {
      const snap = await getDocs(collection(db!, 'category'));
      return sortOnboardingOptions(
        snap.docs
          .map((categoryDoc, index): OnboardingOption | null => {
            const data = categoryDoc.data();
            if (data.isActive === false) return null;
            const label = normalizeLabel(
              typeof data.title === 'string' && data.title.trim()
                ? data.title
                : categoryDoc.id
            );
            if (!label) return null;
            return {
              id: categoryDoc.id,
              label,
              order: numericOrder(data.order, (index + 1) * 10),
            };
          })
          .filter((item): item is OnboardingOption => Boolean(item))
      );
    }

    const snap = await getDocs(collection(db!, 'wordbank', meta.id, 'words'));
    return sortOnboardingOptions(
      snap.docs
        .map((optionDoc, index): OnboardingOption | null => {
          const data = optionDoc.data();
          if (data.isActive === false) return null;
          const label = normalizeLabel(
            typeof data.label === 'string' && data.label.trim()
              ? data.label
              : optionDoc.id
          );
          if (!label) return null;
          return {
            id: optionDoc.id,
            label,
            order: numericOrder(data.order, (index + 1) * 10),
          };
        })
        .filter((item): item is OnboardingOption => Boolean(item))
    );
  };

  const loadOnboardingList = async (selectionId: OnboardingSelectionId) => {
    const meta = ONBOARDING_SELECTIONS.find((item) => item.id === selectionId)!;
    setLoadingLists((state) => new Set(state).add(selectionId));
    setError(null);
    try {
      const rows = await readOnboardingList(meta);
      setOptions((prev) => ({ ...prev, [selectionId]: rows }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingLists((state) => {
        const next = new Set(state);
        next.delete(selectionId);
        return next;
      });
    }
  };

  useEffect(() => {
    ONBOARDING_SELECTIONS.forEach((selection) => {
      loadOnboardingList(selection.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistOnboardingOption = async (
    meta: OnboardingSelectionMeta,
    option: OnboardingOption
  ) => {
    const payload = {
      label: option.label,
      order: option.order,
      isActive: true,
      updatedAt: Date.now(),
      updatedBy: userEmail ?? null,
    };

    if (meta.source === 'category') {
      await setDoc(
        doc(db!, 'category', option.id),
        {
          title: option.label,
          ...payload,
        },
        { merge: true }
      );
      return;
    }

    await setDoc(doc(db!, 'wordbank', meta.id, 'words', option.id), payload, { merge: true });
  };

  const handleAddOnboardingOption = async (e: React.FormEvent) => {
    e.preventDefault();
    const label = normalizeLabel(draft);
    if (!label) return;
    if (optionExists(options[active], label)) {
      setError(`"${label}" is already in ${activeMeta.label}.`);
      return;
    }

    const order = (activeOptions[activeOptions.length - 1]?.order ?? 0) + 10;
    const id = activeMeta.source === 'category'
      ? categoryDocId(label)
      : wordSlug(label);
    if (!id) {
      setError('Use at least one letter or number in the option label.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const nextOption = { id, label, order };
      await persistOnboardingOption(activeMeta, nextOption);
      setOptions((prev) => ({
        ...prev,
        [active]: sortOnboardingOptions([...prev[active], nextOption]),
      }));
      setDraft('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveOnboardingOption = async (option: OnboardingOption) => {
    if (!window.confirm(`Remove "${option.label}" from ${activeMeta.label}?`)) return;
    setSaving(true);
    setError(null);
    try {
      if (activeMeta.source === 'category') {
        await setDoc(
          doc(db!, 'category', option.id),
          {
            isActive: false,
            removedAt: Date.now(),
            removedBy: userEmail ?? null,
            updatedAt: Date.now(),
            updatedBy: userEmail ?? null,
          },
          { merge: true }
        );
      } else {
        await deleteDoc(doc(db!, 'wordbank', activeMeta.id, 'words', option.id));
      }

      setOptions((prev) => ({
        ...prev,
        [active]: prev[active].filter((item) => item.id !== option.id),
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleMoveOnboardingOption = async (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= activeOptions.length) return;

    const reordered = [...activeOptions];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    const renumbered = reordered.map((option, nextIndex) => ({
      ...option,
      order: (nextIndex + 1) * 10,
    }));

    setSaving(true);
    setError(null);
    try {
      await Promise.all(
        renumbered.map((option) => persistOnboardingOption(activeMeta, option))
      );
      setOptions((prev) => ({ ...prev, [active]: renumbered }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSeedDefaults = async () => {
    const defaults = activeMeta.defaults ?? [];
    const missing = defaults
      .map(normalizeLabel)
      .filter((label) => label && !optionExists(options[active], label));
    if (!missing.length) {
      setError('Current defaults are already present.');
      return;
    }

    const lastOrder = activeOptions[activeOptions.length - 1]?.order ?? 0;
    const rows = missing.map((label, index): OnboardingOption => ({
      id: wordSlug(label),
      label,
      order: lastOrder + (index + 1) * 10,
    }));

    setSaving(true);
    setError(null);
    try {
      await Promise.all(rows.map((option) => persistOnboardingOption(activeMeta, option)));
      setOptions((prev) => ({
        ...prev,
        [active]: sortOnboardingOptions([...prev[active], ...rows]),
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden mt-10">
      <div className="px-6 py-4 border-b border-neutral-100 flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-neutral-900">Onboarding selections</div>
          <div className="text-xs text-neutral-500 mt-0.5">
            Chip options used during onboarding. iOS falls back to bundled defaults if a list
            is empty or below the required pick count.
          </div>
        </div>
        <button
          type="button"
          onClick={() => { void loadOnboardingList(active); }}
          disabled={isLoadingActive || saving}
          className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-700 hover:border-neutral-400 transition disabled:opacity-50"
        >
          {isLoadingActive ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <div className="px-6 py-4 border-b border-neutral-100 bg-neutral-50">
        <nav className="flex gap-2 flex-wrap">
          {ONBOARDING_SELECTIONS.map((selection) => {
            const isActive = selection.id === active;
            return (
              <button
                key={selection.id}
                type="button"
                onClick={() => {
                  setActive(selection.id);
                  setDraft('');
                  setError(null);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                  isActive
                    ? 'bg-neutral-900 text-white border-neutral-900'
                    : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400'
                }`}
              >
                {selection.label}{' '}
                <span className={isActive ? 'text-neutral-400' : 'text-neutral-400'}>
                  · {options[selection.id].length}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="px-6 py-4 border-b border-neutral-100 flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-neutral-900">{activeMeta.label}</div>
          <div className="text-xs text-neutral-500 mt-0.5">{activeMeta.hint}</div>
        </div>
        {activeMeta.defaults && (
          <button
            type="button"
            onClick={handleSeedDefaults}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs font-semibold text-neutral-700 hover:border-neutral-400 transition disabled:opacity-50"
          >
            Seed current defaults
          </button>
        )}
      </div>

      <form onSubmit={handleAddOnboardingOption} className="px-6 py-4 border-b border-neutral-100">
        <div className="flex gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Add to ${activeMeta.label.toLowerCase()}…`}
            className="flex-1 px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400 bg-white"
          />
          <button
            type="submit"
            disabled={!draft.trim() || saving}
            className="px-4 py-2 bg-neutral-900 text-white text-sm font-semibold rounded-lg disabled:opacity-40 hover:bg-neutral-800 transition"
          >
            Add
          </button>
        </div>
      </form>

      {error && (
        <div className="px-6 py-3 bg-red-50 text-red-700 text-xs border-b border-red-100">
          {error}
        </div>
      )}

      {!isLoadingActive && activeOptions.length < activeMeta.requiredCount && (
        <div className="px-6 py-3 bg-yellow-50 text-yellow-800 text-xs border-b border-yellow-100">
          Keep at least {activeMeta.requiredCount} active options for this onboarding step.
        </div>
      )}

      {isLoadingActive ? (
        <div className="px-6 py-12 text-center text-sm text-neutral-500">Loading…</div>
      ) : activeOptions.length === 0 ? (
        <div className="px-6 py-12 text-center text-sm text-neutral-500">
          No onboarding options yet.
        </div>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {activeOptions.map((option, index) => (
            <li
              key={option.id}
              className="px-6 py-3 flex items-center justify-between gap-4 hover:bg-neutral-50"
            >
              <div className="min-w-0">
                <span className="font-mono text-sm">{option.label}</span>
                <div className="mt-1 text-[10px] font-mono text-neutral-400 truncate">
                  {activeMeta.source === 'category' ? 'category' : `wordbank/${activeMeta.id}`}
                  {' '}· order {option.order}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => { void handleMoveOnboardingOption(index, -1); }}
                  disabled={index === 0 || saving}
                  className="px-2 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-700 hover:border-neutral-400 transition disabled:opacity-40"
                >
                  Up
                </button>
                <button
                  type="button"
                  onClick={() => { void handleMoveOnboardingOption(index, 1); }}
                  disabled={index === activeOptions.length - 1 || saving}
                  className="px-2 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-700 hover:border-neutral-400 transition disabled:opacity-40"
                >
                  Down
                </button>
                <button
                  type="button"
                  onClick={() => { void handleRemoveOnboardingOption(option); }}
                  disabled={saving}
                  className="text-xs text-neutral-400 hover:text-red-600 transition disabled:opacity-40"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export const WordbankAdmin: React.FC = () => {
  const { user, signOutUser } = useAuth();
  const [active, setActive] = useState<ListId>('fillerwords');
  const [words, setWords] = useState<Record<ListId, string[]>>({
    fillerwords: [],
    weakWords: [],
    confidentWords: [],
    wod: [],
  });
  const [loadingLists, setLoadingLists] = useState<Set<ListId>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [filter, setFilter] = useState('');
  const [wotdWordStats, setWotdWordStats] = useState<Record<string, WotdWordStats>>({});
  const [wotdPronunciationOverrides, setWotdPronunciationOverrides] = useState<
    Record<string, WotdPronunciationOverride | null>
  >({});
  const [wotdPronunciationDraft, setWotdPronunciationDraft] = useState('');
  const [currentWotd, setCurrentWotd] = useState<CurrentWotdSelection>(
    emptyCurrentWotdSelection
  );
  const [wotdSelectionDraft, setWotdSelectionDraft] = useState('');
  const [wotdSelectionLoading, setWotdSelectionLoading] = useState(false);
  const [wotdSelectionSaving, setWotdSelectionSaving] = useState(false);
  const [wotdQueueSaving, setWotdQueueSaving] = useState(false);
  const [wotdAudioStatuses, setWotdAudioStatuses] = useState<Record<string, WotdAudioStatus>>({});
  const [wotdAudioLoading, setWotdAudioLoading] = useState(false);
  const [wotdAudioGenerating, setWotdAudioGenerating] = useState(false);
  const [wotdAudioProgress, setWotdAudioProgress] = useState<string | null>(null);
  const [playingWotdWord, setPlayingWotdWord] = useState<string | null>(null);
  const wotdPreviewAudioRef = useRef<HTMLAudioElement | null>(null);

  const patchWotdAudioStatus = (word: string, patch: Partial<WotdAudioStatus>) => {
    setWotdAudioStatuses((prev) => ({
      ...prev,
      [word]: {
        ...(prev[word] ?? emptyAudioStatus(word)),
        ...patch,
      },
    }));
  };

  const releaseWotdPreviewAudio = useCallback(() => {
    const audio = wotdPreviewAudioRef.current;
    if (!audio) return;
    audio.onplay = null;
    audio.onended = null;
    audio.onerror = null;
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    wotdPreviewAudioRef.current = null;
  }, []);

  const stopWotdAudioPreview = () => {
    releaseWotdPreviewAudio();
    setPlayingWotdWord(null);
  };

  const handlePreviewWotdAudio = async (word: string, remoteURL: string) => {
    if (playingWotdWord === word) {
      stopWotdAudioPreview();
      return;
    }

    setError(null);
    stopWotdAudioPreview();
    patchWotdAudioStatus(word, { error: null });

    const audio = new Audio(remoteURL);
    wotdPreviewAudioRef.current = audio;
    audio.onplay = () => setPlayingWotdWord(word);
    audio.onended = () => stopWotdAudioPreview();
    audio.onerror = () => {
      patchWotdAudioStatus(word, { error: 'Audio preview failed. Try regenerating this file.' });
      stopWotdAudioPreview();
    };

    try {
      await audio.play();
    } catch (e) {
      patchWotdAudioStatus(word, {
        error: e instanceof Error ? e.message : 'Audio preview failed.',
      });
      stopWotdAudioPreview();
    }
  };

  useEffect(() => () => {
    releaseWotdPreviewAudio();
  }, [releaseWotdPreviewAudio]);

  const hydrateWotdAudioStatus = async (targetWords: string[] = words.wod) => {
    const s = storage;
    if (!s || targetWords.length === 0) return;

    setWotdAudioLoading(true);
    try {
      const entries = await Promise.all(
        targetWords.map(async (word): Promise<[string, WotdAudioStatus]> => {
          const storagePath = wotdAudioStoragePath(word);
          try {
            const ref = storageRef(s, storagePath);
            const [remoteURL, meta] = await Promise.all([
              getDownloadURL(ref),
              getMetadata(ref),
            ]);
            return [
              word,
              {
                storagePath,
                remoteURL,
                remoteUpdatedAt: new Date(meta.updated).getTime(),
                generating: false,
                error: null,
              },
            ];
          } catch {
            return [word, emptyAudioStatus(word)];
          }
        })
      );

      setWotdAudioStatuses((prev) => {
        const next = { ...prev };
        entries.forEach(([word, status]) => {
          next[word] = {
            ...status,
            generating: prev[word]?.generating ?? false,
          };
        });
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setWotdAudioLoading(false);
    }
  };

  const loadCurrentWotdSelection = async () => {
    setWotdSelectionLoading(true);
    setError(null);
    try {
      const snap = await getDoc(doc(db!, CONFIG_COLLECTION, WOTD_CURRENT_CONFIG_DOC_ID));
      const selection = snap.exists()
        ? currentWotdSelectionFromData(snap.data() as Record<string, unknown>)
        : emptyCurrentWotdSelection();
      setCurrentWotd(selection);
      setWotdSelectionDraft(selection.reason);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setWotdSelectionLoading(false);
    }
  };

  const loadList = async (listId: ListId) => {
    setLoadingLists((s) => new Set(s).add(listId));
    setError(null);
    try {
      const snap = await getDocs(collection(db!, 'wordbank', listId, 'words'));
      const ids = snap.docs.map((d) => d.id).sort((a, b) => a.localeCompare(b));
      setWords((w) => ({ ...w, [listId]: ids }));
      if (listId === 'wod') {
        setWotdWordStats(
          snap.docs.reduce<Record<string, WotdWordStats>>((acc, wordDoc) => {
            acc[wordDoc.id] = wotdWordStatsFromData(
              wordDoc.data() as Record<string, unknown>
            );
            return acc;
          }, {})
        );
        setWotdPronunciationOverrides(
          snap.docs.reduce<Record<string, WotdPronunciationOverride | null>>(
            (acc, wordDoc) => {
              acc[wordDoc.id] = wotdPronunciationOverrideFromData(
                wordDoc.data() as Record<string, unknown>
              );
              return acc;
            },
            {}
          )
        );
        await hydrateWotdAudioStatus(ids);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingLists((s) => {
        const next = new Set(s);
        next.delete(listId);
        return next;
      });
    }
  };

  useEffect(() => {
    LISTS.forEach((l) => {
      loadList(l.id);
    });
    void loadCurrentWotdSelection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleWords = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const list = words[active];
    const filtered = q ? list.filter((w) => w.toLowerCase().includes(q)) : list;
    if (active !== 'wod') return filtered;
    return sortWotdWordsByPriority(filtered, wotdWordStats);
  }, [words, active, filter, wotdWordStats]);

  const authoredWotdAudioCount = useMemo(
    () => words.wod.filter((word) => Boolean(wotdAudioStatuses[word]?.remoteURL)).length,
    [words.wod, wotdAudioStatuses]
  );

  const loadWotdVoiceConfig = async (): Promise<WotdVoiceConfig> => {
    const snap = await getDoc(doc(db!, CONFIG_COLLECTION, WOTD_VOICE_CONFIG_DOC_ID));
    return normalizeWotdVoiceConfig(
      snap.exists() ? (snap.data() as Partial<WotdVoiceConfig>) : undefined
    );
  };

  const generateWotdPronunciation = async (
    word: string,
    configOverride?: WotdVoiceConfig
  ) => {
    const s = storage;
    if (!s) {
      setError('Firebase Storage is not configured.');
      return;
    }

    setError(null);
    if (playingWotdWord === word) {
      stopWotdAudioPreview();
    }
    patchWotdAudioStatus(word, { generating: true, error: null });

    try {
      const config = configOverride ?? (await loadWotdVoiceConfig());
      const pronunciationOverride = wotdPronunciationOverrides[word] ?? null;
      const resp = await fetch('/api/tts-wotd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: word,
          voice: config.voiceId,
          presetId: config.presetId,
          settings: shouldUseVoiceDefaults(config.presetId) ? null : config.elevenLabsSettings,
          punctuationPauses: false,
          pronunciationOverride,
        }),
      });

      if (!resp.ok) {
        let message = `Generate failed (HTTP ${resp.status})`;
        try {
          const payload = await resp.json();
          message = payload?.error || payload?.detail || message;
        } catch {
          /* ignore */
        }
        throw new Error(message);
      }

      const blob = await resp.blob();
      const storagePath = wotdAudioStoragePath(word);
      const ref = storageRef(s, storagePath);
      await uploadBytes(ref, blob, {
        contentType: 'audio/mpeg',
        cacheControl: 'no-cache',
        customMetadata: {
          word,
          voiceId: config.voiceId,
          presetId: config.presetId,
          modelId: 'eleven_flash_v2',
          languageCode: 'en',
          generatedAt: String(Date.now()),
          source: 'wordbank-admin',
          ...(pronunciationOverride
            ? {
                pronunciationAlphabet: pronunciationOverride.alphabet,
                pronunciationOverride: pronunciationOverride.phoneme,
              }
            : {}),
        },
      });

      const [remoteURL, meta] = await Promise.all([
        getDownloadURL(ref),
        getMetadata(ref),
      ]);
      patchWotdAudioStatus(word, {
        storagePath,
        remoteURL,
        remoteUpdatedAt: new Date(meta.updated).getTime(),
        generating: false,
        error: null,
      });
    } catch (e) {
      patchWotdAudioStatus(word, {
        generating: false,
        error: e instanceof Error ? e.message : String(e),
      });
    }
  };

  const handleGenerateMissingWotdAudio = async () => {
    const s = storage;
    if (!s) {
      setError('Firebase Storage is not configured.');
      return;
    }

    const targets = words.wod.filter((word) => !wotdAudioStatuses[word]?.remoteURL);
    if (!targets.length) {
      setWotdAudioProgress('All WOTD pronunciations are already generated.');
      return;
    }

    setWotdAudioGenerating(true);
    setWotdAudioProgress(null);
    try {
      const config = await loadWotdVoiceConfig();
      for (let i = 0; i < targets.length; i += 1) {
        const word = targets[i];
        setWotdAudioProgress(`${i + 1}/${targets.length}: generating ${word}`);
        await generateWotdPronunciation(word, config);
      }
      setWotdAudioProgress('WOTD pronunciation generation finished.');
    } finally {
      setWotdAudioGenerating(false);
    }
  };

  const handleSelectWotd = async (word: string) => {
    const reason = normalizeLabel(wotdSelectionDraft);

    const selectedBy = user?.email ?? null;
    const dateKey = currentUtcDayKey();
    const now = Date.now();

    setWotdSelectionSaving(true);
    setError(null);
    try {
      const selection = await runTransaction(db!, async (transaction) => {
        const configRef = doc(db!, CONFIG_COLLECTION, WOTD_CURRENT_CONFIG_DOC_ID);
        const wordRef = doc(db!, 'wordbank', 'wod', 'words', word);
        const wordSnap = await transaction.get(wordRef);
        const existingWordStats = wordSnap.exists()
          ? wotdWordStatsFromData(wordSnap.data() as Record<string, unknown>)
          : emptyWotdWordStats();
        const shouldIncrement = existingWordStats.lastWordOfTheDayDateKey !== dateKey;

        const configPayload = {
          word,
          wordOfTheDay: word,
          reason,
          selectionReason: reason,
          mode: 'manual',
          source: 'wordbank-admin',
          dateKey,
          selectedAt: now,
          selectedBy,
          updatedAt: now,
          updatedBy: selectedBy,
        };

        const wordPayload: Record<string, unknown> = {
          lastWordOfTheDayAt: now,
          lastWordOfTheDayBy: selectedBy,
          lastWordOfTheDayReason: reason,
          lastWordOfTheDayDateKey: dateKey,
          updatedAt: now,
          updatedBy: selectedBy,
        };
        if (shouldIncrement) {
          wordPayload.timesWordOfTheDay = increment(1);
        }

        transaction.set(configRef, configPayload, { merge: true });
        transaction.set(wordRef, wordPayload, { merge: true });

        return {
          selection: currentWotdSelectionFromData(configPayload),
          incremented: shouldIncrement,
        };
      });

      setCurrentWotd(selection.selection);
      setWotdSelectionDraft(reason);
      setWotdWordStats((prev) => {
        const existing = prev[word] ?? emptyWotdWordStats();
        return {
          ...prev,
          [word]: {
            timesWordOfTheDay:
              existing.timesWordOfTheDay + (selection.incremented ? 1 : 0),
            lastWordOfTheDayAt: now,
            lastWordOfTheDayBy: selectedBy,
            lastWordOfTheDayReason: reason,
            lastWordOfTheDayDateKey: dateKey,
          },
        };
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setWotdSelectionSaving(false);
    }
  };

  const handleQueueWotd = async (word: string) => {
    const queuedBy = user?.email ?? null;
    const now = Date.now();

    setWotdQueueSaving(true);
    setError(null);
    try {
      await setDoc(
        doc(db!, CONFIG_COLLECTION, WOTD_CURRENT_CONFIG_DOC_ID),
        {
          queuedWord: word,
          queuedWordOfTheDay: word,
          queuedReason: '',
          queuedSource: 'wordbank-admin',
          queuedUpdatedAt: now,
          queuedUpdatedBy: queuedBy,
          updatedAt: now,
          updatedBy: queuedBy,
        },
        { merge: true }
      );
      setCurrentWotd((prev) => ({
        ...prev,
        queuedWord: word,
        queuedReason: '',
        queuedUpdatedAt: now,
        queuedUpdatedBy: queuedBy,
        queuedSource: 'wordbank-admin',
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setWotdQueueSaving(false);
    }
  };

  const handleEditWotdPronunciation = async (word: string) => {
    const current = wotdPronunciationOverrides[word]?.phoneme ?? '';
    const next = window.prompt(
      'Optional CMU ARPAbet phones for this English pronunciation. Leave blank to use normal TTS.',
      current
    );
    if (next === null) return;

    const phoneme = next.trim() ? parseCmuPronunciation(next) : null;
    if (next.trim() && !phoneme) {
      setError('Use CMU ARPAbet phones with spaces, such as S UW2 P ER0 IH0 M P OW1 Z.');
      return;
    }

    const pronunciationOverride: WotdPronunciationOverride | null = phoneme
      ? { alphabet: 'cmu-arpabet', phoneme }
      : null;

    setError(null);
    try {
      await setDoc(
        doc(db!, 'wordbank', 'wod', 'words', word),
        {
          pronunciationOverride,
          pronunciationOverrideUpdatedAt: Date.now(),
          pronunciationOverrideUpdatedBy: user?.email ?? null,
          updatedAt: Date.now(),
          updatedBy: user?.email ?? null,
        },
        { merge: true }
      );
      setWotdPronunciationOverrides((prev) => ({
        ...prev,
        [word]: pronunciationOverride,
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const raw = draft.trim().toLowerCase();
    if (!raw) return;
    if (words[active].some((w) => w.toLowerCase() === raw)) {
      setError(`"${raw}" is already in ${active}.`);
      return;
    }

    const pronunciationPhoneme =
      active === 'wod' && wotdPronunciationDraft.trim()
        ? parseCmuPronunciation(wotdPronunciationDraft)
        : null;
    if (active === 'wod' && wotdPronunciationDraft.trim() && !pronunciationPhoneme) {
      setError('Use CMU ARPAbet phones with spaces, such as S UW2 P ER0 IH0 M P OW1 Z.');
      return;
    }

    const pronunciationOverride: WotdPronunciationOverride | null = pronunciationPhoneme
      ? { alphabet: 'cmu-arpabet', phoneme: pronunciationPhoneme }
      : null;

    const payload: Record<string, unknown> = {
      addedAt: Date.now(),
      addedBy: user?.email ?? null,
    };
    if (active === 'wod' && pronunciationOverride) {
      payload.pronunciationOverride = pronunciationOverride;
      payload.pronunciationOverrideUpdatedAt = Date.now();
      payload.pronunciationOverrideUpdatedBy = user?.email ?? null;
    }

    setError(null);
    try {
      await setDoc(doc(db!, 'wordbank', active, 'words', raw), payload);
      setWords((w) => ({ ...w, [active]: [...w[active], raw].sort((a, b) => a.localeCompare(b)) }));
      if (active === 'wod') {
        setWotdWordStats((prev) => ({
          ...prev,
          [raw]: emptyWotdWordStats(),
        }));
        setWotdPronunciationOverrides((prev) => ({
          ...prev,
          [raw]: pronunciationOverride,
        }));
        patchWotdAudioStatus(raw, emptyAudioStatus(raw));
        void hydrateWotdAudioStatus([raw]);
        setWotdPronunciationDraft('');
      }
      setDraft('');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const handleDelete = async (word: string) => {
    if (!window.confirm(`Remove "${word}" from ${active}?`)) return;
    setError(null);
    try {
      if (active === 'wod' && playingWotdWord === word) {
        stopWotdAudioPreview();
      }
      await deleteDoc(doc(db!, 'wordbank', active, 'words', word));
      setWords((w) => ({ ...w, [active]: w[active].filter((x) => x !== word) }));
      if (active === 'wod') {
        setWotdWordStats((prev) => {
          const next = { ...prev };
          delete next[word];
          return next;
        });
        setWotdAudioStatuses((prev) => {
          const next = { ...prev };
          delete next[word];
          return next;
        });
        setWotdPronunciationOverrides((prev) => {
          const next = { ...prev };
          delete next[word];
          return next;
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const activeMeta = LISTS.find((l) => l.id === active)!;
  const isLoadingActive = loadingLists.has(active);
  const todayKey = currentUtcDayKey();
  const currentWotdWord = currentWotd.word
    ? words.wod.find((word) => word.toLowerCase() === currentWotd.word?.toLowerCase())
      ?? currentWotd.word
    : null;
  const queuedWotdWord = currentWotd.queuedWord
    ? words.wod.find((word) => word.toLowerCase() === currentWotd.queuedWord?.toLowerCase())
      ?? currentWotd.queuedWord
    : null;
  const nextAutoWotdCandidate = sortWotdWordsByPriority(
    words.wod,
    wotdWordStats,
    [currentWotdWord]
  )[0] ?? null;
  const wordOnDeck = queuedWotdWord ?? nextAutoWotdCandidate;
  const hasExplicitQueuedWord = Boolean(queuedWotdWord);
  const currentWotdIsForToday = Boolean(
    currentWotdWord && currentWotd.dateKey === todayKey
  );
  const currentWotdStats = currentWotdWord ? wotdWordStats[currentWotdWord] : null;
  const currentWotdSelectedAt = formatAdminTimestamp(currentWotd.selectedAt);
  const queuedWotdUpdatedAt = formatAdminTimestamp(currentWotd.queuedUpdatedAt);

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-lg font-bold">Wordbank</div>
            <div className="text-xs text-neutral-500">
              Edits land in Firestore immediately. iOS picks them up on next app launch.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 font-mono">{user?.email}</span>
            <button
              onClick={signOutUser}
              className="text-xs text-neutral-600 underline hover:text-neutral-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-6">
          <AdminTabs current="wordbank" />
        </div>
        <nav className="flex gap-2 mb-6 flex-wrap">
          {LISTS.map((l) => {
            const isActive = l.id === active;
            const count = words[l.id].length;
            return (
              <button
                key={l.id}
                onClick={() => {
                  setActive(l.id);
                  setFilter('');
                  setError(null);
                  if (l.id !== 'wod') {
                    setWotdPronunciationDraft('');
                  }
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                  isActive
                    ? 'bg-neutral-900 text-white border-neutral-900'
                    : 'bg-white text-neutral-700 border-neutral-200 hover:border-neutral-400'
                }`}
              >
                {l.label}{' '}
                <span className={isActive ? 'text-neutral-400' : 'text-neutral-400'}>
                  · {count}
                </span>
              </button>
            );
          })}
        </nav>

        <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-100 flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-neutral-900">{activeMeta.label}</div>
              <div className="text-xs text-neutral-500 mt-0.5">{activeMeta.hint}</div>
            </div>
            {active === 'wod' && (
              <div className="flex items-center gap-2 flex-wrap justify-end shrink-0">
                <span className="text-xs text-neutral-500 font-mono">
                  {authoredWotdAudioCount}/{words.wod.length || '—'} audio
                </span>
                <button
                  onClick={() => { void hydrateWotdAudioStatus(words.wod); }}
                  disabled={wotdAudioLoading || wotdAudioGenerating || !storage}
                  className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-700 hover:border-neutral-400 transition disabled:opacity-50"
                >
                  {wotdAudioLoading ? 'Refreshing…' : 'Refresh'}
                </button>
                <button
                  onClick={handleGenerateMissingWotdAudio}
                  disabled={wotdAudioLoading || wotdAudioGenerating || !storage || !words.wod.length}
                  className="px-3 py-1.5 rounded-lg bg-neutral-900 text-white text-xs font-semibold hover:bg-neutral-800 transition disabled:opacity-50"
                >
                  {wotdAudioGenerating ? 'Generating…' : 'Generate missing'}
                </button>
              </div>
            )}
          </div>

          {active === 'wod' && (
            <div className="px-6 py-4 border-b border-neutral-100 bg-neutral-50">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Current Word of the Day
                  </div>
                  <div className="mt-1 text-sm font-semibold text-neutral-900">
                    {currentWotdIsForToday && currentWotdWord
                      ? currentWotdWord
                      : 'No word selected for the current UTC day'}
                  </div>
                  {currentWotdIsForToday && (
                    <div className="mt-1 text-xs text-neutral-500">
                      {currentWotdSelectedAt ? `Selected ${currentWotdSelectedAt}` : 'Selected today'}
                      {currentWotd.selectedBy ? ` by ${currentWotd.selectedBy}` : ''}
                      {currentWotd.mode ? ` · ${currentWotd.mode}` : ''}
                      {currentWotdStats ? ` · ${currentWotdStats.timesWordOfTheDay} times featured` : ''}
                    </div>
                  )}
                  {currentWotdIsForToday && currentWotd.reason && (
                    <div className="mt-2 text-xs text-neutral-700">
                      {currentWotd.reason}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => { void loadCurrentWotdSelection(); }}
                  disabled={wotdSelectionLoading || wotdSelectionSaving}
                  className="px-3 py-1.5 rounded-lg border border-neutral-200 bg-white text-xs text-neutral-700 hover:border-neutral-400 transition disabled:opacity-50"
                >
                  {wotdSelectionLoading ? 'Refreshing…' : 'Refresh pick'}
                </button>
              </div>

              <div className="mt-4 pt-4 border-t border-neutral-200 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
                    Word on deck
                  </div>
                  <div className="mt-1 text-sm font-semibold text-neutral-900">
                    {wordOnDeck ?? 'No queued word yet'}
                  </div>
                  <div className="mt-1 text-xs text-neutral-500">
                    {hasExplicitQueuedWord
                      ? 'Queued for the next automatic rollover.'
                      : 'Fallback preview based on least-featured priority.'}
                    {' '}Auto-pick runs just after midnight UTC.
                    {queuedWotdUpdatedAt ? ` Updated ${queuedWotdUpdatedAt}` : ''}
                    {currentWotd.queuedUpdatedBy ? ` by ${currentWotd.queuedUpdatedBy}` : ''}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  Why this word today? <span className="text-neutral-400">(optional)</span>
                </label>
                <textarea
                  value={wotdSelectionDraft}
                  onChange={(e) => setWotdSelectionDraft(e.target.value)}
                  rows={2}
                  placeholder="Optional note for why this word deserves focus today."
                  className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400 bg-white resize-y"
                />
                <div className="mt-1 text-[11px] text-neutral-500">
                  Choose a word below to feature it today, or queue it as the next automatic pick. Counts update once per word per UTC day, and least-featured words stay near the top.
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleAdd} className="px-6 py-4 border-b border-neutral-100 bg-neutral-50">
            <div className="flex gap-2">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={`Add to ${activeMeta.label.toLowerCase()}…`}
                className="flex-1 px-3 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400 bg-white"
              />
              <button
                type="submit"
                disabled={!draft.trim()}
                className="px-4 py-2 bg-neutral-900 text-white text-sm font-semibold rounded-lg disabled:opacity-40 hover:bg-neutral-800 transition"
              >
                Add
              </button>
            </div>
            {active === 'wod' && (
              <div className="mt-3">
                <label className="block text-xs font-medium text-neutral-700 mb-1">
                  English pronunciation override
                </label>
                <input
                  value={wotdPronunciationDraft}
                  onChange={(e) => setWotdPronunciationDraft(e.target.value)}
                  placeholder="Optional CMU ARPAbet, e.g. S UW2 P ER0 IH0 M P OW1 Z"
                  className="w-full px-3 py-2 text-sm font-mono border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400 bg-white"
                />
                <div className="mt-1 text-[11px] text-neutral-500">
                  Leave blank for normal English TTS. Add phonemes here for words that need a precise pronunciation.
                </div>
              </div>
            )}
          </form>

          <div className="px-6 py-3 border-b border-neutral-100">
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter…"
              className="w-full px-3 py-1.5 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:border-neutral-400 bg-white"
            />
          </div>

          {error && (
            <div className="px-6 py-3 bg-red-50 text-red-700 text-xs border-b border-red-100">{error}</div>
          )}

          {active === 'wod' && !storage && (
            <div className="px-6 py-3 bg-yellow-50 text-yellow-800 text-xs border-b border-yellow-100">
              Firebase Storage is not configured. Add the web app storage env vars before generating files.
            </div>
          )}

          {active === 'wod' && wotdAudioProgress && (
            <div className="px-6 py-3 bg-blue-50 text-blue-700 text-xs border-b border-blue-100">
              {wotdAudioProgress}
            </div>
          )}

          {isLoadingActive ? (
            <div className="px-6 py-12 text-center text-sm text-neutral-500">Loading…</div>
          ) : visibleWords.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-neutral-500">
              {filter ? `No matches for "${filter}".` : 'No words yet. Add the first one above.'}
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {visibleWords.map((w) => {
                const audio = wotdAudioStatuses[w] ?? emptyAudioStatus(w);
                const stats = wotdWordStats[w] ?? emptyWotdWordStats();
                const age = remoteAgeLabel(audio.remoteUpdatedAt);
                const audioURL = audio.remoteURL
                  ? cacheBustedAudioURL(audio.remoteURL, audio.remoteUpdatedAt)
                  : null;
                const pronunciationOverride = wotdPronunciationOverrides[w] ?? null;
                const isFeaturedToday =
                  active === 'wod'
                  && currentWotd.dateKey === todayKey
                  && currentWotd.word?.toLowerCase() === w.toLowerCase();
                const isQueuedNext =
                  active === 'wod'
                  && queuedWotdWord?.toLowerCase() === w.toLowerCase();
                const lastFeaturedAt = formatAdminTimestamp(stats.lastWordOfTheDayAt);
                return (
                  <li key={w} className="px-6 py-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4 hover:bg-neutral-50">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm">{w}</span>
                        {isFeaturedToday && (
                          <span className="px-2 py-0.5 rounded-full bg-neutral-900 text-white text-[10px] font-semibold">
                            Featured today
                          </span>
                        )}
                        {isQueuedNext && (
                          <span className="px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 text-[10px] font-semibold">
                            On deck
                          </span>
                        )}
                      </div>
                      {active === 'wod' && (
                        <>
                          <div className="mt-1 text-[10px] font-mono text-neutral-400 truncate">
                            {audio.storagePath}
                          </div>
                          <div className="mt-1 text-[11px] text-neutral-500">
                            {stats.timesWordOfTheDay} times featured
                            {stats.lastWordOfTheDayDateKey ? ` · last ${stats.lastWordOfTheDayDateKey}` : ''}
                            {lastFeaturedAt ? ` at ${lastFeaturedAt}` : ''}
                          </div>
                          {pronunciationOverride && (
                            <div className="mt-1 text-[10px] font-mono text-neutral-500">
                              CMU {pronunciationOverride.phoneme}
                            </div>
                          )}
                        </>
                      )}
                      {active === 'wod' && audio.error && (
                        <div className="mt-1 text-xs text-red-600">{audio.error}</div>
                      )}
                    </div>

                    <div className="flex items-center justify-end gap-2 shrink-0 flex-wrap">
                      {active === 'wod' && (
                        <>
                          <button
                            type="button"
                            onClick={() => { void handleQueueWotd(w); }}
                            disabled={wotdQueueSaving}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50 ${
                              isQueuedNext
                                ? 'border border-neutral-900 text-neutral-900 bg-white hover:bg-neutral-100'
                                : 'border border-neutral-200 text-neutral-700 hover:border-neutral-400'
                            }`}
                          >
                            {isQueuedNext ? 'Queued next' : 'Queue next'}
                          </button>
                          <button
                            type="button"
                            onClick={() => { void handleSelectWotd(w); }}
                            disabled={wotdSelectionSaving}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition disabled:opacity-50 ${
                              isFeaturedToday
                                ? 'border border-neutral-900 text-neutral-900 bg-white hover:bg-neutral-100'
                                : 'border border-neutral-200 text-neutral-700 hover:border-neutral-400'
                            }`}
                          >
                            {isFeaturedToday ? 'Update note' : 'Feature today'}
                          </button>
                          {audio.generating ? (
                            <span className="text-xs text-neutral-500">Generating…</span>
                          ) : audioURL ? (
                            <>
                              <a
                                href={audioURL}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs text-green-700 hover:underline"
                              >
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                Audio{age ? ` · ${age}` : ''}
                              </a>
                              <button
                                type="button"
                                onClick={() => { void handlePreviewWotdAudio(w, audioURL); }}
                                className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs font-semibold text-neutral-700 hover:border-neutral-400 transition"
                              >
                                {playingWotdWord === w ? 'Stop' : 'Play'}
                              </button>
                            </>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 text-xs text-neutral-400">
                              <span className="w-1.5 h-1.5 rounded-full bg-neutral-300" />
                              Missing audio
                            </span>
                          )}

                          <button
                            type="button"
                            onClick={() => { void handleEditWotdPronunciation(w); }}
                            className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition ${
                              pronunciationOverride
                                ? 'border-neutral-900 text-neutral-900 bg-white hover:bg-neutral-100'
                                : 'border-neutral-200 text-neutral-700 hover:border-neutral-400'
                            }`}
                          >
                            {pronunciationOverride ? 'Edit pronunciation' : 'Add pronunciation'}
                          </button>

                          <button
                            type="button"
                            onClick={() => { void generateWotdPronunciation(w); }}
                            disabled={audio.generating || wotdAudioGenerating || !storage}
                            className="px-3 py-1.5 rounded-lg border border-neutral-200 text-xs font-semibold text-neutral-700 hover:border-neutral-400 transition disabled:opacity-50"
                          >
                            {audio.remoteURL ? 'Regenerate' : 'Generate'}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(w)}
                        className="text-xs text-neutral-400 hover:text-red-600 transition"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="text-xs text-neutral-400 mt-6 text-center">
          {visibleWords.length} of {words[active].length} shown
        </div>

        <OnboardingSelectionsPanel userEmail={user?.email ?? null} />
      </main>
    </div>
  );
};
